import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Box,
  Typography,
  Avatar,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import Business from '@mui/icons-material/Business';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import Person from '@mui/icons-material/Person';
import Dashboard from '@mui/icons-material/Dashboard';
import { useAuth } from '../contexts/AuthContext';
import { UserType } from '../data/types/auth';

const getRoleDisplay = (userType: string | null) => {
  switch (userType?.toLowerCase()) {
    case UserType.SUPER_ADMIN:
      return {
        icon: <AdminPanelSettings fontSize="small" />,
        label: 'Super Admin',
      };
    case UserType.BUSINESS:
      return {
        icon: <Business fontSize="small" />,
        label: 'Business Account',
      };
    case UserType.USER:
      return {
        icon: <Person fontSize="small" />,
        label: 'Standard Account',
      };
    default:
      return {
        icon: <Person fontSize="small" />,
        label: 'Account',
      };
  }
};

export const UserMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, userType, signOut } = useAuth();
  const navigate = useNavigate();
  const roleDisplay = getRoleDisplay(userType);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
    handleClose();
  };

  return (
    <Box>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
        sx={{ color: 'text.primary' }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          {user?.email?.[0].toUpperCase()}
        </Avatar>
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {user?.email}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mt: 0.5,
            color: 'text.secondary',
          }}>
            {roleDisplay.icon}
            <Typography variant="body2">
              {roleDisplay.label}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 1 }} />
        {userType?.toLowerCase() === UserType.SUPER_ADMIN && (
          <MenuItem onClick={() => { navigate('/manage-business'); handleClose(); }}>
            <ListItemIcon>
              <Business fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manage Business</ListItemText>
          </MenuItem>
        )}
        {userType?.toLowerCase() === UserType.BUSINESS && (
          <MenuItem onClick={() => { navigate('/dashboard'); handleClose(); }}>
            <ListItemIcon>
              <Dashboard fontSize="small" />
            </ListItemIcon>
            <ListItemText>Dashboard</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}; 